import { Box, Button, Grid, Paper, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useState } from 'react';
import { EZPAY_API_URL } from '../util/Constants';
import { ClassifiedLoginError, LoginErrorKind } from '../util/LoginErrorClassifier';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(3),
    },
    title: {
      fontWeight: 'bold',
      marginBottom: theme.spacing(1),
    },
    body: {
      marginBottom: theme.spacing(2),
      wordBreak: 'break-word',
    },
    details: {
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      fontFamily: 'monospace',
      fontSize: '0.8rem',
    },
  }),
);

const getApiHost = (): string => {
  try {
    return new URL(EZPAY_API_URL).host;
  } catch {
    return EZPAY_API_URL;
  }
};

const getMessage = (kind: LoginErrorKind, signedInEmail?: string): { title: string; body: string } => {
  switch (kind) {
    case LoginErrorKind.UserNotFound:
      return {
        title: "We couldn't find your Aptean Pay user",
        body: `${
          signedInEmail ? `You're signed in as ${signedInEmail}, but` : "You're signed in, but"
        } we couldn't find an active Aptean Pay merchant user for that email address. Ask your company's Aptean Pay administrator to check that your user is set up and linked to your company, or sign out and sign in with a different account.`,
      };
    case LoginErrorKind.ApiUnreachable: {
      const apiHost = getApiHost();
      return {
        title: "We couldn't reach Aptean Pay",
        body: `Your browser couldn't connect to ${apiHost}. This is usually a firewall, VPN, proxy or browser extension blocking that address. Try again, or ask your IT team to allow ${apiHost}.`,
      };
    }
    case LoginErrorKind.SignInRejected:
      return {
        title: "We couldn't verify your sign-in",
        body: 'Your sign-in may have expired. Sign out, then sign in again.',
      };
    case LoginErrorKind.ServerError:
      return {
        title: 'Aptean Pay ran into a problem',
        body: 'Something went wrong on our side while loading your account. Please try again in a few minutes.',
      };
    default:
      return {
        title: "We couldn't load your account",
        body: 'Something went wrong while loading your account. Please try again.',
      };
  }
};

interface LoginErrorDetailsProps {
  error: ClassifiedLoginError;
  signedInEmail?: string;
  correlationId?: string;
  occurredAt: string;
  onRetry: () => void;
  onSignOut: () => void;
}

const LoginErrorDetails: React.FC<LoginErrorDetailsProps> = props => {
  const { error, signedInEmail, correlationId, occurredAt, onRetry, onSignOut } = props;
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const { title, body } = getMessage(error.kind, signedInEmail);

  const detailLines = [
    `Signed in as: ${signedInEmail ?? 'unknown'}`,
    `Error code: ${error.code}`,
    error.statusCode !== undefined ? `HTTP status: ${error.statusCode}` : undefined,
    correlationId ? `Reference: ${correlationId}` : undefined,
    `Time: ${occurredAt}`,
    `Page: ${window.location.host}`,
    error.detail ? `Detail: ${error.detail.slice(0, 200)}` : undefined,
  ].filter(line => !!line);
  const detailText = detailLines.join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(detailText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Paper className={classes.paper}>
      <Typography component="h1" variant="h6" color="textPrimary" className={classes.title}>
        {title}
      </Typography>
      <Typography color="textPrimary" className={classes.body}>
        {body}
      </Typography>
      <Typography variant="body2" color="textSecondary" className={classes.body}>
        If this keeps happening, contact Aptean Pay support and include the details below.
      </Typography>
      <Box className={classes.details} data-cy="login-error-details">
        {detailText}
      </Box>
      <Grid container spacing={1} justifyContent="flex-end">
        <Grid item>
          <Button variant="text" size="small" onClick={handleCopy} data-cy="copy-login-error-details">
            {copied ? 'COPIED' : 'COPY DETAILS'}
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" size="small" onClick={onSignOut} data-cy="login-error-sign-out">
            SIGN OUT
          </Button>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" size="small" onClick={onRetry} data-cy="login-error-retry">
            TRY AGAIN
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LoginErrorDetails;
